import { type ClassValue, clsx } from 'clsx'
import React, { ReactElement } from 'react'

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

interface Reference {
  id: string
  label?: string
  videoTitle?: string
  url?: string
  youtubeUrl?: string
  videoId?: string
  position?: number
}

interface VideoReference {
  id: string
  videoTitle: string
  videoId?: string
  youtubeUrl?: string
}

// Unicode superscript digits
const SUPERSCRIPT_DIGITS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹']

/**
 * Processes text containing citation patterns (^[refX]) and converts them to React elements
 * with proper superscript formatting and clickable links.
 */
export function processCitations(text: string, references?: Reference[]): (string | ReactElement)[] {
  if (!references || references.length === 0) {
    // If no references provided, just return the text with citations stripped
    return [text.replace(/\^\[[^\]]+\]/g, '')]
  }

  // Create a map of reference IDs to reference objects for quick lookup
  const refMap = new Map(references.map(ref => [ref.id, ref]))

  // Split text by citation pattern while keeping the citations
  const parts: (string | ReactElement)[] = []
  let lastIndex = 0
  const citationRegex = /\^\[([^\]]+)\]/g
  let match

  while ((match = citationRegex.exec(text)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    // Add citation as React element
    const refId = match[1]
    const reference = refMap.get(refId)
    
    if (reference) {
      const citationElement = React.createElement(
        'sup',
        { 
          key: `citation-${refId}-${match.index}`, 
          className: 'citation' 
        },
        (reference.url || reference.youtubeUrl)
          ? React.createElement(
              'a',
              {
                href: reference.url || reference.youtubeUrl,
                target: '_blank',
                rel: 'noreferrer',
                className: 'citation-link'
              },
              `[${refId}]`
            )
          : React.createElement(
              'span',
              { className: 'citation-text' },
              `[${refId}]`
            )
      )
      parts.push(citationElement)
    } else {
      // If reference not found, just add the raw citation text
      parts.push(`[${refId}]`)
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text after last citation
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

/**
 * Converts a number to superscript format using Unicode characters
 */
function toSuperscript(num: number): string {
  return num.toString().split('').map(digit => SUPERSCRIPT_DIGITS[parseInt(digit)]).join('')
}

/**
 * Processes citations with video-based numbering system.
 * Maps citation references to actual video numbers shown in the sidebar.
 */
export function processCitationsWithVideoNumbers(
  text: string, 
  references?: Reference[], 
  videoList?: VideoReference[]
): (string | ReactElement)[] {
  if (!references || references.length === 0 || !videoList || videoList.length === 0) {
    // If no references or videos, strip citations
    return [text.replace(/\^\[[^\]]+\]/g, '')]
  }

  // Create mapping from reference videoId to video position in sidebar
  const videoPositionMap = new Map()
  videoList.forEach((video, index) => {
    if (video.videoId) {
      videoPositionMap.set(video.videoId, index + 1) // 1-based numbering
    }
  })

  // Split text by citation pattern
  const parts: (string | ReactElement)[] = []
  let lastIndex = 0
  const citationRegex = /\^\[([^\]]+)\]/g
  let match

  while ((match = citationRegex.exec(text)) !== null) {
    // Add text before citation
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    // Find the reference for this citation
    const refId = match[1]
    const reference = references.find(ref => ref.id === refId)
    
    if (reference && reference.videoId) {
      const videoNumber = videoPositionMap.get(reference.videoId)
      if (videoNumber) {
        const citationElement = React.createElement(
          'sup',
          { 
            key: `citation-${refId}-${match.index}`, 
            className: 'citation-number',
            title: reference.videoTitle || `Video ${videoNumber}`
          },
          reference.youtubeUrl 
            ? React.createElement(
                'a',
                {
                  href: reference.youtubeUrl,
                  target: '_blank',
                  rel: 'noreferrer',
                  className: 'citation-link'
                },
                toSuperscript(videoNumber)
              )
            : toSuperscript(videoNumber)
        )
        parts.push(citationElement)
      } else {
        // Fallback to reference ID if video not found
        parts.push(`[${refId}]`)
      }
    } else {
      // Fallback if reference not found
      parts.push(`[${refId}]`)
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}
